/**
 * Módulo Oferta Formulário
 * Renderiza formulário de oferta com validação completa e integração com API
 */

import BaseModule from "./BaseModule.js";
import {
  createSubmitHandler,
  setupBirthdateControl,
  setupInputMasks,
} from "./handlers/ofertaFormularioHandlers.js";
import { setupMultiStepHandlers } from "./handlers/ofertaFormularioMultiStepHandlers.js";
import { setupNoCepOfertaHandlers } from "./handlers/ofertaFormularioNoCepOfertaHandlers.js";
import {
  isDraftOffer,
  normalizeOfertaData,
  resolveOfferId,
} from "./helpers/ofertaFormularioHelpers.js";
import { buildFormHTML } from "./renderers/ofertaFormularioRenderer.js";
import { getOfertaFormularioStyles } from "./styles/ofertaFormularioStyles.js";
import { buildUtmParams, preloadClientIpAddress } from "./utils/formApi.js";
import { initGclidTracking } from "./utils/gclidTracking.js";
import { isMundoZeroKmHost } from "./utils/attributionUtils.js";

export default class OfertaFormularioModule extends BaseModule {
  static defaultConfig = {
    container: null,
    cssPrefix: "mzk-oferta-formulario",
    animateIn: true,
    color: "#00a99d",
    colorBackground: "transparent",
    colorSpan: "#5a6372",
    colorTitle: "#1a1a1a",
    fontFamily: '"Inter", sans-serif',
    title0km: "Oferta 0km",
    titleForm:
      "Preencha seus dados para um Consultor entrar em contato com você!",
    textHeader: "",
    isImageMobile: true,
    origin: "widget-form",
    // utmSource/utmMedium sem default fixo: a engine de atribuição
    // (attributionUtils.js) já resolve URL > Google Ads > cookie do
    // portal > config > orgânico — um default fixo aqui bloquearia a
    // consulta ao cookie e o fallback orgânico calculado pelo referrer.
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    utmContent: null,
    utmTerm: null,
  };

  constructor(config = {}) {
    super({ ...OfertaFormularioModule.defaultConfig, ...config });
    this.ofertaData = null;
    this.utmParams = {};
    this.shadow = null;
  }

  async init() {
    // Processa e normaliza dados da oferta
    if (this.config.oferta) {
      this.ofertaData = normalizeOfertaData(this.config.oferta);
    }

    // Modelo de formulário vindo do Strapi: config.modelos é um array (o editor pode
    // selecionar mais de um modelo — padrao | form-2-steps | form-no-cep). Quando há mais
    // de um, sorteia um aleatoriamente a cada carregamento da página (init roda uma vez
    // por instância do widget). Array ausente/vazio (registros antigos) é tratado como
    // ['form-2-steps'], nunca como 'padrao', para não regredir LPs multi-step já publicadas.
    const modelosDisponiveis =
      Array.isArray(this.config.modelos) && this.config.modelos.length > 0
        ? this.config.modelos
        : ["form-2-steps"];
    this.modelo =
      modelosDisponiveis[Math.floor(Math.random() * modelosDisponiveis.length)];

    preloadClientIpAddress();

    // Verifica se é rascunho
    if (isDraftOffer(this.ofertaData, this.config)) {
      console.warn(
        "⚠️ [OfertaFormularioModule] Oferta em rascunho - módulo não será inicializado",
      );
      this.initialized = false;
      return;
    }

    // Mescla configurações do backend com defaults
    this.mergeConfigurations();

    // Captura UTMs da URL e mescla com configuração do backend
    this.utmParams = buildUtmParams(this.config, this.ofertaData, undefined, {
      formModel: this.modelo,
    });

    // Persiste gclid da URL em cookie/localStorage com expiração de 30 dias
    initGclidTracking();

    // Inicializa Google Analytics
    this.initGoogleAnalytics();

    this.initialized = true;
  }

  mergeConfigurations() {
    // Prioriza valores vindos do backend, com fallbacks para defaults
    this.config.color =
      this.config.color || this.constructor.defaultConfig.color;
    this.config.colorBackground = this.config.colorBackground ?? "transparent";
    this.config.colorSpan =
      this.config.colorSpan || this.constructor.defaultConfig.colorSpan;
    this.config.colorTitle =
      this.config.colorTitle || this.constructor.defaultConfig.colorTitle;
    this.config.fontFamily =
      this.config.fontFamily || this.constructor.defaultConfig.fontFamily;
    this.config.title0km =
      this.config.title0km || this.constructor.defaultConfig.title0km;
    this.config.titleForm =
      this.config.titleForm || this.constructor.defaultConfig.titleForm;
    this.config.textHeader = this.config.textHeader ?? "";
    this.config.isImageMobile =
      this.config.isImageMobile ?? this.constructor.defaultConfig.isImageMobile;
  }

  setupUtmParams() {
    this.utmParams = buildUtmParams(this.config, this.ofertaData, undefined, {
      formModel: this.modelo,
    });
  }

  initGoogleAnalytics() {
    if (typeof window === "undefined") return;

    if (!window.gtag) {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-J036VVBTZ6";
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      window.gtag = gtag;
      window.gtag("js", new Date());
      window.gtag("config", "G-J036VVBTZ6", { send_page_view: false });
    } else {
      window.gtag("config", "G-J036VVBTZ6", { send_page_view: false });
    }

    // Track widget impression
    if (window.gtag) {
      const instanceId = this.getWidgetInstanceId();
      const eventData = {
        position: "wzk-formulario-" + instanceId,
        widget: "Oferta Formulário",
        offer_id: instanceId,
      };

      // widget_version (teste A/B do template: A = form-no-cep + LP de Ofertas,
      // B = form-2-steps ou form-no-cep + LP de Interesse, "" quando o modelo não
      // faz parte do teste, ex: "padrao") só é reportado dentro do portal
      // mundozerokm.com.br — sites de parceiros/clientes que embedam o widget
      // não entram nesse teste.
      if (isMundoZeroKmHost()) {
        eventData.widget_version = this.getWidgetVersion();
      }

      window.gtag("event", "widget_impression", eventData);
    }
  }

  /**
   * Teste A/B do template de formulário: A = form-no-cep + LP de Ofertas,
   * B = form-2-steps ou form-no-cep + LP de Interesse, "" fora do teste (ex: "padrao").
   */
  getWidgetVersion() {
    if (this.modelo === "form-no-cep") {
      return this.ofertaData?.isOfferlp === true ? "A" : "B";
    }
    if (this.modelo === "form-2-steps") return "B";
    return "";
  }

  /**
   * Identificador da instância pra eventos/posição. Ordem de prioridade canônica
   * (SPEC.md > SDD "padronizacao-identificador-oferta-ga" §4.1): hash/campaign_identifier
   * primeiro (mesmo campo usado no Motor de Leads), depois documentId, e só por
   * último `config.id` — que só existe no fluxo normal (Widget Modular buscado do
   * Strapi via widget-loader.js); na API standalone (renderOfertaFormularioStandalone,
   * usada pelas notícias) não há `id` de Widget Modular, então cai no identificador
   * da própria oferta. Decisão consciente: `position` (widget_impression/widget_click,
   * abaixo) herda esta mudança automaticamente — quebra a série histórica dessa
   * dimensão no GA, aceito por decisão explícita do usuário neste ciclo SDD.
   */
  getWidgetInstanceId() {
    return resolveOfferId(this.ofertaData, this.config);
  }

  /** Dispara no primeiro clique em qualquer lugar do widget (ver setupEventListeners). */
  trackWidgetClick() {
    if (!window.gtag) return;

    const instanceId = this.getWidgetInstanceId();
    const eventData = {
      position: "wzk-formulario-" + instanceId,
      widget: "Oferta Formulário",
      offer_id: instanceId
    };

    // Mesma regra do widget_impression: widget_version só é reportado dentro
    // do portal mundozerokm.com.br.
    if (isMundoZeroKmHost()) {
      eventData.widget_version = this.getWidgetVersion();
    }

    window.gtag("event", "widget_click", eventData);
  }

  render(container) {
    if (!this.initialized) {
      console.warn(
        "⚠️ [OfertaFormularioModule] Módulo não inicializado - renderização cancelada",
      );
      return;
    }

    this.container = container || this.config.container;
    if (!this.container) {
      throw new Error("Container é obrigatório");
    }

    // Cria Shadow DOM
    this.shadow = this.container.attachShadow({ mode: "open" });

    // Injeta estilos
    const styleEl = document.createElement("style");
    styleEl.textContent = getOfertaFormularioStyles(this.config);
    this.shadow.appendChild(styleEl);

    // Renderiza formulário conforme o modelo (padrao = legado single-step;
    // form-2-steps e form-no-cep usam o container genérico controlado pelo handler)
    if (this.modelo === "padrao") {
      // Single-step legado: renderiza diretamente
      const wrapper = document.createElement("div");
      wrapper.innerHTML = buildFormHTML(this.config, this.ofertaData);
      this.shadow.appendChild(wrapper.firstElementChild);
    } else {
      // form-2-steps, form-no-cep (Interesse ou Ofertas): cria container e
      // deixa o handler correspondente controlar o layout/navegação
      const formContainer = document.createElement("div");
      formContainer.id = `${this.config.cssPrefix}-form-container`;

      // Class test_a/test_b pra identificação visual do teste A/B — só dentro
      // do portal mundozerokm.com.br (mesma regra dos eventos de tracking).
      if (isMundoZeroKmHost()) {
        const widgetVersion = this.getWidgetVersion();
        if (widgetVersion === "A") formContainer.classList.add("test_a");
        else if (widgetVersion === "B") formContainer.classList.add("test_b");
      }

      this.shadow.appendChild(formContainer);
    }

    // Configura event listeners
    this.setupEventListeners();

    if (this.config.animateIn) {
      this.animateIn();
    }
  }

  setupEventListeners() {
    // widget_click: dispara uma vez, no primeiro clique em qualquer lugar do
    // widget (independente do modelo padrao/2-steps/no-cep).
    this.shadow.addEventListener("click", () => this.trackWidgetClick(), {
      once: true,
    });

    if (this.modelo === "padrao") {
      // Single-step legado: usa handler tradicional
      const { cssPrefix } = this.config;

      // Máscaras de input
      setupInputMasks(this.shadow, cssPrefix, this.getWidgetInstanceId());

      // Controle de placeholder para data
      setupBirthdateControl(this.shadow, cssPrefix);

      // Submit do formulário
      const form = this.shadow.getElementById(`${cssPrefix}-form`);
      form.addEventListener("submit", createSubmitHandler(this));
      return;
    }

    const isOfertaNoCep =
      this.modelo === "form-no-cep" && this.ofertaData?.isOfferlp === true;

    if (isOfertaNoCep) {
      // form-no-cep + LP de Ofertas: 1 step único, sem CEP
      setupNoCepOfertaHandlers(
        this.shadow,
        this.config,
        this.ofertaData,
        this.showMessage.bind(this),
        this.hideMessage.bind(this),
      );
      return;
    }

    // form-2-steps (padrão do widget modular) e form-no-cep + LP de Interesse
    // reaproveitam o mesmo handler multi-step; noCep desliga CEP e força Estado/Cidade
    setupMultiStepHandlers(
      this.shadow,
      this.config,
      this.ofertaData,
      this.showMessage.bind(this),
      this.hideMessage.bind(this),
      { noCep: this.modelo === "form-no-cep" },
    );
  }

  showMessage(text, type) {
    const { cssPrefix } = this.config;
    const msgBox = this.shadow.getElementById(`${cssPrefix}-message`);

    if (msgBox) {
      msgBox.textContent = text;
      msgBox.className = `${cssPrefix}-message`;
      msgBox.classList.add(type);
      msgBox.style.display = "block";
    }
  }

  hideMessage() {
    const { cssPrefix } = this.config;
    const msgBox = this.shadow.getElementById(`${cssPrefix}-message`);

    if (msgBox) {
      msgBox.style.display = "none";
      msgBox.className = `${cssPrefix}-message`;
    }
  }

  animateIn() {
    const element = this.shadow.querySelector(
      `.${this.config.cssPrefix}-wrapper`,
    );
    if (element) {
      element.classList.add("fade-in");
    }
  }

  static getStyles() {
    return (config) => getOfertaFormularioStyles(config);
  }

  destroy() {
    if (this.shadow) {
      this.shadow.innerHTML = "";
    }
    super.destroy();
  }
}
